/**
 * Gallery Page - WoodAlps Visual Showcase
 * 
 * This interactive gallery page features:
 * - Tabbed interface for different content categories (Bikes, Engravings)
 * - Responsive image grid with modal viewing
 * - Sticky navigation for seamless browsing
 * - Social media integration for brand engagement
 * - Dynamic content loading from Sanity CMS
 * - Custom portable text rendering for descriptions
 * 
 * Gallery Features:
 * - Two main categories: Bikes and Engravings
 * - Sticky tab navigation with smooth scrolling
 * - Modal image viewer with navigation
 * - Responsive grid layout for all screen sizes
 * - Image optimization with Next.js Image component
 * 
 * Interactive Elements:
 * - Tab switching with active state indicators
 * - Sticky navigation that adapts to scroll position
 * - Social media contact integration
 * - Smooth transitions and hover effects
 * 
 * @description Interactive image gallery with tabbed navigation and modal viewing
 */

import Head from 'next/head';
import Link from 'next/link';
import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import { getGalleryItems, getEngravingItems } from '../src/app/studio/lib/sanity';
import Image from 'next/image';
import Layout from '../components/layout/Layout';
import Gallery from '../components/gallery/Gallery';
import SocialMediaContact from '../components/sections/SocialMediaContact';
import { PortableText } from '@portabletext/react';
import { useTranslation } from '../src/hooks/useTranslation';
import TabButton from '../components/ui/TabButton';

// Custom portable text components for gallery descriptions
const portableTextComponents = {
  marks: {
    neuropol: ({children}) => <span className="font-neuropol">{children}</span>,
    montserrat: ({children}) => <span className="font-montserrat">{children}</span>,
    strong: ({children}) => <strong>{children}</strong>,
    em: ({children}) => <em>{children}</em>,
  },
  block: {
    normal: ({children}) => <p className="mb-4 last:mb-0">{children}</p>,
  },
};



export default function GalleryPage({ galleryItems, engravingItems }) {
  const router = useRouter();
  const contentRef = useRef(null);
  const tabsRef = useRef(null);
  const tabContentRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [activeTab, setActiveTab] = useState('bikes');
  const [isTabsSticky, setIsTabsSticky] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { t } = useTranslation(); 
  // Social section data
  const socialSection = {
    title: t('socialSection.title'),
    fallbackDescription: t('socialSection.description'),
    facebookLink: 'https://facebook.com/woodalps',
    instagramLink: 'https://www.instagram.com/woodalps_wooden_bikes/'
  };

  // Contact info data
  const contactInfo = {
    title: t('contactSection.title'),
    fallbackDescription: t('contactSection.description'),
    buttonText: t('contactSection.buttonText'),
    buttonLink:  '/contact'
  };

  // Check for tab URL parameter on initial load
  useEffect(() => {
    const { tab } = router.query;
    if (tab === 'engravings') {
      setActiveTab('engravings');
    }
  }, [router.query]);

  // Get header height on mount and window resize
  useEffect(() => {
    const updateHeaderHeight = () => {
      const header = document.querySelector('header');
      if (header) {
        setHeaderHeight(header.offsetHeight + 40);
      }
    };

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, []);

  // Check if we're on mobile on mount and window resize
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Track when tabs become sticky
  useEffect(() => {
    const handleScroll = () => {
      if (tabsRef.current) {
        const tabsTop = tabsRef.current.getBoundingClientRect().top;
        const isMobileView = window.innerWidth < 768;
        
        if (isMobileView) {
          // Mobile: smooth detection to prevent flickering
          setIsTabsSticky(tabsTop <= 5);
        } else {
          // Desktop: original simple detection
          setIsTabsSticky(tabsTop <= headerHeight);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [headerHeight]);

  // Function to handle tab changes and scroll to content
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    
    // Update URL with selected tab (without page reload)
    router.push({
      pathname: router.pathname,
      query: { ...(tabId !== 'bikes' ? { tab: tabId } : {}) }
    }, undefined, { shallow: true });
    
    // Only scroll if the tabs are in sticky mode
    if (isTabsSticky) {
      // After state update, scroll to the top of the tab content
      setTimeout(() => {
        if (tabContentRef.current) {
          // Calculate the offset by considering the tab height and adding some breathing room
          const tabHeight = tabsRef.current ? tabsRef.current.offsetHeight : 0;
          const yOffset = headerHeight + tabHeight;
          
          const tabContentTop = tabContentRef.current.getBoundingClientRect().top + window.pageYOffset;
          
          window.scrollTo({
            top: tabContentTop - yOffset - 12,
            behavior: 'smooth'
          });
        }
      }, 10);
    }
  };

  const renderPortableText = (portableText, fallbackText) => {
    // Check if portableText exists, is an array, has content, and that content has text
    if (portableText && 
        Array.isArray(portableText) && 
        portableText.length > 0 && 
        portableText.some(block => 
          block.children && 
          block.children.length > 0 && 
          block.children.some(child => child.text && child.text.trim().length > 0)
        )
    ) {
      return <PortableText value={portableText} components={portableTextComponents} />;
    }
    // Return fallback text if any of the above conditions aren't met
    return <p>{fallbackText}</p>;
  };

  return (
    <>
      <Head>
        <title>{t('gallery.meta_title')}</title>
        <meta
          name="description"
          content={t('gallery.meta_description')}
        />
      </Head>

      {/* Hero Banner */}
      <section className="relative h-[40vh] sm:h-[50vh] md:h-[60vh] w-full overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <Image
            src="/images/home-galleryPreview-cows.jpg"
            alt="WoodAlps Blog"
            fill
            sizes="100vw"
            className="object-cover"
            priority
          />
        </div>
        
        {/* Overlay Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-transparent"></div>

        {/* Content */}
        <div className="relative h-full flex flex-col items-center justify-center px-4 sm:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-2 sm:mb-4 font-neuropol">
            {t('gallery.title')}
          </h1>
        </div>
      </section>

      {/* Add spacing between hero and tabs */}
      <div className={`bg-wood-brown/10 ${isMobile ? 'pt-4' : 'pt-16 md:pt-24'}`}></div>

      {/* Tab Navigation */}
      <div 
        ref={tabsRef}
        className={`sticky z-10 ${
          isMobile 
            ? `transition-all duration-300 ease-in-out ${isTabsSticky ? 'bg-black/95 backdrop-blur-sm shadow-lg' : 'bg-wood-brown/10'}`
            : `transition-colors duration-100 ${isTabsSticky ? 'bg-black' : 'bg-wood-brown/10'}`
        }`}
        style={{ 
          top: isMobile ? '0px' : `${headerHeight}px`
        }}
      >
        {/* Responsive tabs container */}
        <div className={isMobile ? "px-2" : ""}>
          <div className={
            isMobile 
              ? `max-w-5xl mx-auto flex justify-center overflow-x-auto border-b border-wood-brown/30 hide-scrollbar mobile-tabs-scroll ${isTabsSticky ? 'py-2 tabs-sticky-mobile' : 'py-0'}`
              : `max-w-3xl mx-auto flex justify-center border-b ${isTabsSticky ? 'border-wood-brown/30' : 'border-wood-brown/20'}`
          }>
            <TabButton 
              isActive={activeTab === 'bikes'}
              onClick={() => handleTabChange('bikes')}
              label={t('gallery.bikes')}
              isSticky={isTabsSticky}
            />
            <TabButton 
              isActive={activeTab === 'engravings'}
              onClick={() => handleTabChange('engravings')}
              label={t('gallery.engravings')}
              isSticky={isTabsSticky}
            />
          </div>
        </div>
      </div>

      {/* Gallery Section */}
      <section className="py-10 pb-10 sm:pb-16 md:pb-20 bg-wood-brown/10">
        <div className="container mx-auto px-4 sm:px-6">
          <div ref={tabContentRef} className="tab-content min-h-[500px] max-w-6xl mx-auto">
            {/* Bikes Tab Content */}
            {activeTab === 'bikes' && (
              <div className="bikes-tab">
                {galleryItems && galleryItems.length > 0 ? (
                  <Gallery items={galleryItems} />
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500 text-lg">{t('gallery.no_bike_gallery_items')}</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Engravings Tab Content */}
            {activeTab === 'engravings' && (
              <div className="engravings-tab">
                {engravingItems && engravingItems.length > 0 ? (
                  <Gallery items={engravingItems} />
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500 text-lg">{t('gallery.no_engraving_gallery_items')}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      
      {/* Social Media and Contact */}
      <section className="section bg-wood-brown/10 py-24">
        <div className="container-custom">
          <SocialMediaContact 
            containerClassName="bg-white p-10 mx-auto justify-center items-center rounded-xl"
            socialSection={socialSection}
            contactInfo={contactInfo}
            renderPortableText={renderPortableText}
          />
        </div>
      </section>
    </>
  );
}

// Use Layout component as the page layout
GalleryPage.getLayout = function getLayout(page) {
  return <Layout transparentHeader={true} headerShadow={true}>{page}</Layout>;
};

export async function getStaticProps({ preview = false, locale = 'en' }) {
  const galleryItems = await getGalleryItems(preview, locale);
  const engravingItems = await getEngravingItems(preview, locale);
  
  return {
    props: {
      galleryItems,
      engravingItems,
    },
    revalidate: process.env.VERCEL_ENV === 'preview' ? 1 : 86400, // 24 hours
  };
}
